import React, { useState, useRef } from "react";
import { MODULES, MODULE_ORDER } from "../services/modulesApi";
import { useToolOrder } from "../utils/toolOrder"; // 2026-10-26 per-person card order (users.prefs.tool_order)
import ModuleIcon from "./ModuleIcon";
import { ui, EmptyState } from "./common/ui";

const CSS = `
.al-card { transition: border-color var(--t-fast), box-shadow var(--t-fast), background var(--t-fast); }
.al-card:hover { border-color: var(--border-strong) !important; box-shadow: var(--shadow-md) !important; }
.al-card:hover .al-open { color: var(--accent); }
.al-card:hover .al-arrow { transform: translateX(3px); }
.al-card:focus-visible { outline: none; box-shadow: var(--focus-ring) !important; border-color: var(--accent) !important; }
.al-item { position: relative; display: flex; }
.al-item > .al-card { flex: 1; min-width: 0; cursor: grab; }
.al-item.al-drag { opacity: .45; }
.al-ctl { position: absolute; top: 8px; right: 8px; display: flex; align-items: center; gap: 2px; opacity: 0; transition: opacity var(--t-fast); }
.al-item:hover .al-ctl, .al-item:focus-within .al-ctl { opacity: 1; }
.al-mv { width: 24px; height: 24px; border: 1px solid var(--border); background: var(--card); color: var(--text-muted); border-radius: var(--radius-sm); cursor: pointer; font: inherit; font-size: 12px; line-height: 1; padding: 0; }
.al-mv:hover:not(:disabled) { color: var(--accent); border-color: var(--border-strong); }
.al-mv:focus-visible { outline: none; box-shadow: var(--focus-ring); }
.al-mv:disabled { opacity: .35; cursor: default; }
.al-grip { color: var(--text-muted); font-size: 13px; letter-spacing: -2px; padding: 0 4px; cursor: grab; user-select: none; }
.al-reset { background: none; border: none; color: var(--text-muted); font: inherit; font-size: 12.5px; cursor: pointer; text-decoration: underline; padding: 0; }
.al-reset:hover { color: var(--accent); }
@media (hover: none) { .al-ctl { opacity: 1; } .al-grip { display: none; } }
.al-arrow { transition: transform .15s; display: inline-block; }
@media (max-width: 640px) {
  .al-wrap { padding: 16px 12px !important; }
  .al-grid { gap: 10px !important; grid-template-columns: 1fr !important; }
  .al-card { padding: 14px !important; min-height: 0 !important; gap: 8px !important; }
  .al-h1 { font-size: 18px !important; }
}
`;

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

export default function AppLauncher({ modules, onOpen, displayName, showToast }) {
  const [order, saveOrder] = useToolOrder(showToast);
  const [preview, setPreview] = useState(null); // live ids while dragging
  const dragId = useRef(null);
  const base = order.filter((m) => modules.includes(m));
  const list = preview || base;
  // Put the reordered visible ids back into the slots of the full order so tools this person can't see keep their place.
  const commit = (ids) => {
    let i = 0;
    saveOrder(order.map((m) => (ids.includes(m) ? ids[i++] : m)));
  };
  const move = (id, d) => {
    const i = base.indexOf(id), j = i + d;
    if (j < 0 || j >= base.length) return;
    const n = base.slice(); n.splice(i, 1); n.splice(j, 0, id);
    commit(n);
  };
  const onOver = (over) => {
    const from = dragId.current;
    if (!from || from === over) return;
    const cur = preview || base;
    const n = cur.filter((x) => x !== from); n.splice(cur.indexOf(over), 0, from);
    if (n.join() !== cur.join()) setPreview(n);
  };
  const endDrag = (save) => {
    const p = preview; dragId.current = null; setPreview(null);
    if (save && p && p.join() !== base.join()) commit(p);
  };
  const isDefault = order.join() === MODULE_ORDER.join();
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="al-wrap" style={{ padding: "20px 24px", width: "100%", boxSizing: "border-box" }}>
      <style>{CSS}</style>
      <h1 className="al-h1" style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2, margin: 0, color: "var(--text)", letterSpacing: "-.01em" }}>
        {greeting()}{displayName ? `, ${displayName}` : ""}
      </h1>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginTop: 4 }}>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{today}</div>
        {list.length > 1 && !isDefault && <button type="button" className="al-reset" onClick={() => saveOrder([])}>Reset order</button>}
      </div>
      <div style={{ height: 20 }} />
      {list.length === 0 ? (
        <div style={ui.card}><EmptyState title="No tools enabled for your login">Ask the Admin to give your team access.</EmptyState></div>
      ) : (
        <div className="al-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
          {list.map((id) => {
            const m = MODULES[id];
            return (
              <div key={id} className={`al-item${dragId.current === id && preview ? " al-drag" : ""}`} data-tool={id}
                draggable onDragStart={(e) => { dragId.current = id; try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); } catch (err) { /* synthetic drag */ } }}
                onDragEnter={() => onOver(id)} onDragOver={(e) => { if (dragId.current) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); endDrag(true); }} onDragEnd={() => endDrag(false)}>
              <button className="al-card" onClick={() => onOpen(id)}
                style={{
                  textAlign: "left", cursor: "pointer", padding: 16, borderRadius: "var(--radius)", minHeight: 168,
                  border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)",
                  boxShadow: "var(--shadow-sm)", display: "flex", flexDirection: "column", gap: 10, font: "inherit",
                }}>
                <span style={{
                  width: 36, height: 36, borderRadius: "var(--radius-sm)", display: "inline-flex", alignItems: "center", justifyContent: "center",
                  color: "var(--accent)", background: "var(--accent-soft)",
                }}>
                  <ModuleIcon id={id} size={20} />
                </span>
                <div style={{ fontSize: 14.5, fontWeight: 650, lineHeight: 1.25, marginTop: 2 }}>{m.title}</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.45, flex: 1 }}>{m.desc}</div>
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end", gap: 8, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
                  <span className="al-open" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)", whiteSpace: "nowrap", transition: "color var(--t-fast)" }}>
                    Open <span className="al-arrow">→</span>
                  </span>
                </div>
              </button>
              {list.length > 1 && (
                <span className="al-ctl">
                  <span className="al-grip" aria-hidden="true">⋮⋮</span>
                  <button type="button" className="al-mv" data-move="left" aria-label={`Move ${m.title} earlier`} title="Move earlier" disabled={list.indexOf(id) === 0} onClick={() => move(id, -1)}>←</button>
                  <button type="button" className="al-mv" data-move="right" aria-label={`Move ${m.title} later`} title="Move later" disabled={list.indexOf(id) === list.length - 1} onClick={() => move(id, 1)}>→</button>
                </span>
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
